/** ArtifactExportReadiness — bundle ready signal + Phase 4 hostedSiteUrl placeholder */

import { useTranslation } from 'react-i18next'
import { CheckCircle, XCircle, ExternalLink } from 'lucide-react'
import { cn } from '../../lib/utils'

interface Props {
  bundleReady: boolean
  /**
   * Phase 4 placeholder (GH-259) — will be populated when hosted-sites feature lands.
   * Kept as optional so this component compiles clean in Phase 1.
   */
  hostedSiteUrl?: string | null
  isDark: boolean
}

export function ArtifactExportReadiness({ bundleReady, hostedSiteUrl, isDark }: Props) {
  const { t } = useTranslation('artifact')

  const rowClass = cn('flex items-center gap-2 py-1.5 text-xs')
  const labelClass = cn('w-32 shrink-0 font-medium', isDark ? 'text-neutral-400' : 'text-neutral-500')
  const valueClass = cn('flex-1', isDark ? 'text-neutral-200' : 'text-neutral-800')

  return (
    <section aria-label={t('exportReadiness.title')} className="space-y-3">
      <h3 className={cn('text-xs font-semibold uppercase tracking-wide', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
        {t('exportReadiness.title')}
      </h3>

      <div className={cn('rounded-lg border p-3 space-y-0.5', isDark ? 'border-white/[0.06] bg-surface-2' : 'border-neutral-200 bg-neutral-50')}>
        {/* Bundle ready */}
        <div className={rowClass}>
          <span className={labelClass}>{t('exportReadiness.bundleReady')}</span>
          <span className={cn('flex items-center gap-1 text-[11px]', bundleReady ? 'text-green-500' : isDark ? 'text-neutral-500' : 'text-neutral-400')}>
            {bundleReady
              ? <><CheckCircle className="h-3 w-3" /> {t('exportReadiness.yes')}</>
              : <><XCircle className="h-3 w-3" /> {t('exportReadiness.no')}</>
            }
          </span>
        </div>

        {/* Hosted site — Phase 4 slot (GH-259) */}
        <div className={rowClass}>
          <span className={labelClass}>{t('exportReadiness.hostedSite')}</span>
          {hostedSiteUrl ? (
            <a
              href={hostedSiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn('flex items-center gap-1 text-[11px] text-brand-400 hover:underline', valueClass)}
            >
              <ExternalLink className="h-3 w-3" />
              {hostedSiteUrl}
            </a>
          ) : (
            <span className={cn('text-[11px]', isDark ? 'text-neutral-600' : 'text-neutral-400')}>
              {t('exportReadiness.notYet')}
            </span>
          )}
        </div>
      </div>
    </section>
  )
}
